import React from 'react';
import {
    Card,
    WarningModal,
    ExamDetailsTabs,
    AppType,
    ExamStatus,
    ExamType,
    ApplicantExamDetailsTabs,
    ExamApplicationStatus,
} from 'components';
import { ExamApplicationDetailsContainer } from '..';
import { ApplicantDraftPhoneModal } from '../../components';
import { connect } from 'react-redux';
import { Button } from 'react-bootstrap';
import { goBack, push } from 'connected-react-router';
import { Dispatch, IRootState } from '../../redux/store/configureStore';
import { useLocalErrorHandler } from 'hooks';
import { ExamApplication } from '../../api/ApiClient';

const ExamDetailsContainer = ({
    onFetchExamApplications,
    onFetchExamDetails,
    setErrorHandler,
    onClearExamApplications,
    examApplicationsList,
    examDetails,
    examId,
    onPush,
    onGoBack,
    apiError,
    getApplicantByPhone,
    applicantDetail,
    applicationDetails,
    getExamApplicationDetails,
    examSessionDetails,
    clearApplicationDetails,
    admitToExam,
}: Props) => {
    useLocalErrorHandler(setErrorHandler);

    const [showApplicantDetails, setShowApplicantDetails] = React.useState(false);
    const [showAddApplicantModal, setShowAddApplicantModal] = React.useState(false);
    const [selectedInReviewExamApplication, setSelectedInReviewExamApplication] = React.useState<ExamApplication | null>(null);
    const [processing, setProcessing] = React.useState(false);
    const [disableSubmitButton, setDisableSubmitButton] = React.useState(true);
    const isApplying = examApplicationsList && examDetails && examDetails.status === ExamStatus.IsApplying;
    const isPaid = applicationDetails && applicationDetails.status === ExamApplicationStatus.Paid;
    const isIntramural = examDetails && examDetails.type === ExamType.Intramural;
    const accessExamStatus = examDetails && (examDetails.status === ExamStatus.ApplyingFinished || examDetails.status === ExamStatus.IsPassing);
    const canAdmitToExam = isPaid && isIntramural && accessExamStatus;

    React.useEffect(() => {
        const fetch = async () => {
            setProcessing(true);
            await onFetchExamDetails(examId);

            await handleListTableChange();
            setProcessing(false);
        };

        fetch();

        return () => {
            onClearExamApplications();
        };
    }, []);

    const handleListTableChange = async (
        searchField: string | undefined = '',
        searchTerm: string | undefined = '',
        page: number | undefined = 1,
        pageSize: number | undefined = 100,
    ) => {
        await onFetchExamApplications({
            examId,
            searchField,
            searchTerm,
            page,
            pageSize,
        });
    };

    const handleItemClick = async (examApplicationId: string) => {
        if (!examDetails) return;

        if (examApplicationsList) {
            const applicant = examApplicationsList.items.find(app => app.examApplicationId === examApplicationId);

            if (applicant && (applicant.status === ExamApplicationStatus.Draft || applicant.status === ExamApplicationStatus.CorrectionsRequired)) {
                onPush(`/exam-draft/${examApplicationId}${examId ? `?examId=${examId}&` : '&'}from=exam-list`);
            } else if (applicant && applicant.status === ExamApplicationStatus.InReview) {
                setSelectedInReviewExamApplication(applicant);
            } else {
                await getExamApplicationDetails(examApplicationId);
                setShowApplicantDetails(true);
            }
        } else {
            await getExamApplicationDetails(examApplicationId);
            setShowApplicantDetails(true);
        }
    };

    const goBackFunc = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const from = urlParams.get('from');

        if (from) {
            onPush(`/${from}`);
        } else {
            onGoBack();
        }
    };

    const onHideWarningModal = () => {
        clearApplicationDetails();
        setShowApplicantDetails(false);
    };

    const onHideReviewModal = () => {
        clearApplicationDetails();
        setSelectedInReviewExamApplication(null);
    };

    const editExperts = () => {
        if (!examDetails) return;

        const urlParams = new URLSearchParams(window.location.search);
        const from = urlParams.get('from');

        onPush(`/exam-experts/${examId}/experts/${examDetails.qualificationId}/${examDetails.qualificationEvaluationCenterId}${from ? `?from=${from}` : ''}`);
    };

    const onAdmitToExam = async () => {
        if (!applicationDetails || !canAdmitToExam) return;

        const admit = await admitToExam(applicationDetails.examApplicationId);

        if (admit) {
            await handleListTableChange();
            onHideWarningModal();
        }
    };

    const onChangeSwitch = (active: boolean[]) => {
        setDisableSubmitButton(!active.every(e => e));
    };

    const renderChangeButton = () => (examDetails && (examDetails.status === ExamStatus.AwaitingExpertConfirmation || examDetails.status === ExamStatus.IsApplying) ? (
        <Button variant="outline-primary" onClick={editExperts} style={{ minWidth: '15.875rem', marginBottom: '70px', marginLeft: '1.875rem' }}>
            Информация и редактирование
        </Button>
    ) : null);

    const renderHeaderButton = () => (isApplying ? (
        <Button
          variant="primary"
          onClick={() => setShowAddApplicantModal(true)}
        >
            Зарегистрировать соискателя
        </Button>
    ) : null);

    const title = examDetails ? examDetails.qualificationTitle : '';

    return (
        <Card
          title={title}
          onClickBack={goBackFunc}
          apiError={apiError}
          showLoader={processing}
          show404Error={!examDetails}
          headerChild={renderHeaderButton()}
          withBackButton
          withoutPadding
          smallTitle
        >
            {examDetails ? (
                <>
                    <ExamDetailsTabs
                      examDetails={examDetails}
                      examApplicationsList={examApplicationsList || undefined}
                      applicantsTabProps={{
                            handleApplicantsListChange: handleListTableChange,
                            handleApplicantClick: handleItemClick,
                        }}
                      appType={AppType.Qec}
                      expertsSignProgressChild={renderChangeButton()}
                      tab={isApplying ? 'applicants' : undefined}
                    />
                    {applicationDetails ? (
                        <WarningModal
                          show={showApplicantDetails}
                          onHide={onHideWarningModal}
                          title={`${applicationDetails.applicant.lastName} ${applicationDetails.applicant.firstName} ${applicationDetails.applicant.middleName}`}
                          onAccess={canAdmitToExam ? onAdmitToExam : undefined}
                          disableAccessButton={canAdmitToExam ? disableSubmitButton : undefined}
                          accessBtnTittle={canAdmitToExam ? 'Допустить к экзамену' : undefined}
                          withoutTitleMargin
                          large
                        >
                            {examDetails && applicationDetails ? (
                                <ApplicantExamDetailsTabs
                                  appType={AppType.Qec}
                                  examDetails={examDetails}
                                  examApplication={applicationDetails}
                                  examSessionDetails={examSessionDetails}
                                  onChangeDocumentSwitch={canAdmitToExam ? onChangeSwitch : undefined}
                                  tab={canAdmitToExam ? 'Documents' : undefined}
                                  resultAndStatusCardCategory={null}
                                  resultAndStatusCardTitle={null}
                                  withoutExpertsTab
                                  withoutInfoTab
                                  withoutRulesTab
                                  inModal
                                />
                            ) : null}
                        </WarningModal>
                    ) : null}

                    {selectedInReviewExamApplication ? (
                        <WarningModal
                          title={selectedInReviewExamApplication.applicantFullName}
                          show={!!selectedInReviewExamApplication}
                          onHide={onHideReviewModal}
                          footerClassNames="p-0"
                          bodyClassNames="p-0"
                          withoutTitleMargin
                          withoutCancelButton
                          large
                        >
                            <ExamApplicationDetailsContainer
                              applicationId={selectedInReviewExamApplication.examApplicationId}
                            />
                        </WarningModal>
                    ) : null}

                    <ApplicantDraftPhoneModal
                      show={showAddApplicantModal}
                      onHide={() => setShowAddApplicantModal(false)}
                      onPush={onPush}
                      getApplicantByPhone={getApplicantByPhone}
                      applicantDetail={applicantDetail}
                      examId={examId}
                    />
                </>
            ) : null}
        </Card>
    );
};

const mapState = (state: IRootState) => ({
    applicantDetail: state.applicants.applicantDetail,
    examApplicationsList: state.examApplications.examApplicationsList,
    examSessionDetails: state.examApplications.examSessionDetails,
    applicationDetails: state.examApplications.applicationDetails,
    examDetails: state.exam.examDetails,
    apiError: state.errors.error,
});

const mapDispatch = (dispatch: Dispatch) => ({
    clearApplicationDetails: dispatch.examApplications.clearApplicationDetails,
    onClearExamApplications: dispatch.examApplications.clearExamApplications,
    onFetchExamDetails: dispatch.exam.fetchExamDetailsAsync,
    getExamApplicationDetails: dispatch.examApplications.getExamApplicationDetailsAsync,
    onFetchExamApplications: dispatch.examApplications.fetchExamApplicationsAsync,
    getApplicantByPhone: dispatch.applicants.getApplicantByPhoneAsync,
    setErrorHandler: dispatch.errors.setErrorHandler,
    admitToExam: dispatch.examApplications.admitToExamAsync,
    onPush: (key: string) => dispatch(push(key)),
    onGoBack: () => dispatch(goBack()),
});

export default connect(mapState, mapDispatch)(ExamDetailsContainer);

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>
type Props = connectedProps & ExamsDetailsOwnProps;

interface ExamsDetailsOwnProps {
    examId: string;
}
